import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const LANGS = [
  { code: "ar", native: "العربية", label: "Arabic" },
  { code: "ru", native: "Русский", label: "Russian" },
  { code: "zh", native: "中文",    label: "Chinese" },
  { code: "fr", native: "Français", label: "French" },
  { code: "ja", native: "日本語",   label: "Japanese" },
  { code: "ko", native: "한국어",   label: "Korean" },
];

const STATUSES = ["not_started", "draft", "review", "published"];
const ENTITIES = ["listing", "project", "community", "journal"];

const STATUS_COLOR = {
  not_started: "bg-gray-100 text-gray-600 border-gray-300",
  draft: "bg-amber-50 text-amber-800 border-amber-200",
  review: "bg-blue-50 text-blue-800 border-blue-200",
  published: "bg-emerald-50 text-emerald-800 border-emerald-200",
};

export default function AdminTranslations() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const [publishing, setPublishing] = useState(false);
  const pollTimer = useRef(null);

  const load = useCallback(() => {
    setBusy(true);
    api.get("/admin/i18n/status")
      .then((r) => setData(r.data))
      .finally(() => setBusy(false));
  }, []);
  useEffect(load, [load]);

  // On mount, pick up any recent job so live progress is visible without
  // having to click Run again in this session.
  useEffect(() => {
    api.get("/admin/translate/latest-job").then(({ data }) => {
      if (data?.job) setJob(data.job);
    }).catch(() => {});
  }, []);

  // Poll the running job every 4s until it completes.
  useEffect(() => {
    if (!job?.id || job.status === "completed" || job.status === "failed") {
      if (pollTimer.current) { clearInterval(pollTimer.current); pollTimer.current = null; }
      return;
    }
    pollTimer.current = setInterval(async () => {
      try {
        const { data: j } = await api.get(`/admin/translate/job/${job.id}`);
        setJob(j);
        if (j.status === "completed" || j.status === "failed") {
          clearInterval(pollTimer.current); pollTimer.current = null;
          load();
          if (j.status === "completed") toast.success(`Translation job done — ${j.done_pairs}/${j.total_pairs} pairs (${j.errors} errors)`);
          else toast.error(`Translation job failed: ${j.error || "unknown"}`);
        }
      } catch { /* ignore transient errors */ }
    }, 4000);
    return () => { if (pollTimer.current) clearInterval(pollTimer.current); };
  }, [job?.id, job?.status, load]);

  const setGlobalStatus = async (lang, status) => {
    try {
      await api.post("/admin/i18n/global-status", { lang, status });
      toast.success(`${lang.toUpperCase()} · site status → ${status}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update status");
    }
  };

  const runBulk = async (entity, lang) => {
    if (!window.confirm(`Machine-translate all ${entity}s into ${lang.toUpperCase()}? Results will be saved as DRAFT — you must publish each one manually.`)) return;
    toast.info("Translating via Emergent LLM… may take up to a minute.");
    try {
      const { data: r } = await api.post(`/admin/translate/bulk?entity=${entity}&langs=${lang}&limit=60`);
      toast.success(`Drafted ${r.processed} ${entity}s in ${lang.toUpperCase()}. Review + publish per doc.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk translation failed");
    }
  };

  const runAll = async () => {
    if (!window.confirm("Machine-translate EVERY listing, project, community and journal into ALL 6 languages? This uses Emergent LLM credits and runs in the background — you can leave this page. Existing translations are skipped.")) return;
    try {
      const { data: r } = await api.post("/admin/translate/run-all");
      if (!r.ok) { toast.error(r.detail || "Job could not start"); return; }
      toast.success(`Translation job queued (id ${r.job_id.slice(0, 8)}). Live progress below.`);
      // Seed a job object; the polling effect will hydrate details.
      setJob({ id: r.job_id, status: "queued", done_pairs: 0, total_pairs: 0, percent: 0, errors: 0, langs: r.langs });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start job");
    }
  };

  const publishAll = async () => {
    if (!window.confirm("Publish ALL draft/review translations across every entity and every language? This makes them public immediately. Continue?")) return;
    setPublishing(true);
    try {
      const { data: r } = await api.post("/admin/i18n/publish-all-drafts");
      const total = Object.values(r.published_per_entity || {}).reduce((a, b) => a + b, 0);
      toast.success(`Published ${total} translation(s). Global languages: ${r.global_langs_published.join(", ").toUpperCase()}.`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Publish-all failed");
    } finally { setPublishing(false); }
  };

  if (busy || !data) return <div className="p-10 text-gray-500">Loading translation status…</div>;

  const jobRunning = job && (job.status === "running" || job.status === "queued");

  return (
    <div className="p-10 max-w-6xl" data-testid="admin-translations">
      <div className="mb-3 text-xs tracking-[0.24em] uppercase text-gray-500">i18n</div>
      <h1 className="font-serif text-4xl mb-2">Translation management</h1>
      <p className="text-sm text-gray-600 max-w-3xl mb-8">
        English is the default and always complete. A language only appears on the public
        site when its <strong>site status</strong> is set to <em>Published</em> AND at least
        some content has been individually published. Missing translations always fall back
        to English — the public site never mixes languages.
      </p>

      {/* Automation card */}
      <div className="bg-[#001588] text-white p-6 mb-10" data-testid="i18n-automation-card">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h2 className="font-serif text-2xl">One-click automation</h2>
            <p className="text-sm text-neutral-200 mt-1 max-w-2xl">
              Translate every listing, project, community and journal into all 6 languages,
              then publish everything in one go. Existing translations are preserved and skipped.
            </p>
          </div>
          <div className="flex flex-col sm:flex-row gap-3 shrink-0">
            <button
              onClick={runAll}
              disabled={jobRunning}
              data-testid="i18n-run-all"
              className="px-5 py-3 bg-[#ffffff] text-black text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-black disabled:opacity-60"
            >
              {jobRunning ? "Job running…" : "Run full bulk translate"}
            </button>
            <button
              onClick={publishAll}
              disabled={publishing}
              data-testid="i18n-publish-all"
              className="px-5 py-3 border border-white text-white text-xs uppercase tracking-[0.2em] hover:bg-white hover:text-[#001588] disabled:opacity-60"
            >
              {publishing ? "Publishing…" : "Publish all drafts"}
            </button>
          </div>
        </div>

        {job && (
          <div className="mt-5 bg-black/25 border border-white/15 p-4" data-testid="i18n-job-progress">
            <div className="flex items-center justify-between text-xs uppercase tracking-[0.2em] text-neutral-200">
              <span>Job {job.id?.slice(0, 8)} · {job.status}</span>
              <span>{job.done_pairs} / {job.total_pairs} pairs · {job.errors || 0} errors</span>
            </div>
            <div className="mt-2 h-2 bg-white/10 overflow-hidden">
              <div
                className="h-full bg-[#ffffff] transition-all"
                style={{ width: `${Math.min(100, job.percent || 0)}%` }}
              />
            </div>
            <div className="mt-2 text-[11px] text-neutral-300">
              {job.status === "completed"
                ? "Completed — drafts saved. Hit ‘Publish all drafts’ above to push them public."
                : job.status === "failed"
                ? `Failed: ${job.error || "unknown"}`
                : "Running in the background — safe to leave this page. Progress updates every ~4 seconds."}
            </div>
          </div>
        )}
      </div>

      {/* Global site-level toggle */}
      <div className="bg-white border border-[#E5E7EB] p-6 mb-10">
        <h2 className="font-serif text-2xl mb-4">Site-level language status</h2>
        <div className="space-y-3">
          {LANGS.map((l) => {
            const currentStatus = data.global?.[l.code] || "not_started";
            return (
              <div key={l.code} className="flex items-center justify-between border-b border-[#F0EEE8] py-3 last:border-b-0" data-testid={`i18n-global-${l.code}`}>
                <div>
                  <span className="text-base font-medium mr-3">{l.native}</span>
                  <span className="text-xs text-gray-500 uppercase tracking-wider">{l.code}</span>
                </div>
                <div className="flex items-center gap-2">
                  {STATUSES.map((s) => (
                    <button
                      key={s}
                      onClick={() => setGlobalStatus(l.code, s)}
                      data-testid={`i18n-global-${l.code}-${s}`}
                      className={`px-3 py-1.5 text-xs uppercase tracking-wider border transition-colors ${
                        currentStatus === s
                          ? "bg-[#001588] text-white border-[#001588]"
                          : `${STATUS_COLOR[s]} hover:border-[#001588]`
                      }`}
                    >{s.replace("_", " ")}</button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Per-entity coverage */}
      <div className="bg-white border border-[#E5E7EB] p-6">
        <h2 className="font-serif text-2xl mb-4">Content coverage</h2>
        <p className="text-sm text-gray-600 mb-6">
          Use &quot;Draft translations&quot; to bulk machine-translate content via Emergent LLM. Each
          result is saved as a draft — nothing is public until you publish per doc from the
          entity&apos;s own admin page or here.
        </p>
        {ENTITIES.map((entity) => {
          const cov = data.coverage?.[entity] || {};
          const total = cov.total || 0;
          return (
            <div key={entity} className="mb-6 last:mb-0" data-testid={`coverage-${entity}`}>
              <div className="text-xs uppercase tracking-[0.2em] text-gray-500 mb-2">
                {entity} · {total} docs
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                {LANGS.map((l) => {
                  const pub = cov.by_lang?.[l.code]?.published || 0;
                  const drf = cov.by_lang?.[l.code]?.draft_or_review || 0;
                  return (
                    <div key={l.code} className="border border-[#E5E7EB] p-3 flex items-center justify-between">
                      <div>
                        <div className="text-sm font-medium">{l.native}</div>
                        <div className="text-xs text-gray-500">
                          <span className="text-emerald-700 font-medium">{pub}</span> published ·
                          <span className="text-amber-700 font-medium"> {drf}</span> draft
                        </div>
                      </div>
                      <button
                        onClick={() => runBulk(entity, l.code)}
                        data-testid={`bulk-${entity}-${l.code}`}
                        className="text-xs uppercase tracking-wider px-3 py-1.5 border border-[#001588] text-[#001588] hover:bg-[#001588] hover:text-white transition-colors"
                      >Draft</button>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
